import React, { useMemo } from 'react';
import classNames from 'classnames';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { Container, WysiwygField, Image } from '@uncinc/uncinc-react-kitchen-sink';


import {
  SCREEN_MOBILE,
  SCREEN_TABLET,
  SCREEN_NORMAL,
  SCREEN_WIDE,
} from '../../../../config/constants';


import { ButtonWrapper, DynamicMediaItem } from '../../../../components';
import type { BlockCTA } from '../../../../types/uncinc_profile/blocks';

import './style.scss';

export interface CtaBlockProps {
  entity: ImmutableEntity<BlockCTA>;
  leftAlign: boolean,
  additonalLinkParams?: string,
}

const CTABlock: React.FC<CtaBlockProps> = ({ entity, leftAlign = false, additonalLinkParams = '' }) => {
  const ctaBlock = entity.toJS();
  const title = ctaBlock.info[0].value;
  const body = ctaBlock.body[0]?.processed;
  const image = ctaBlock.field_media[0];
  const imageAsBackground = ctaBlock.field_use_as_background[0]?.value === true;
  const linkTitle = ctaBlock.field_link[0].title;
  const linkUri = `${ctaBlock.field_link[0].alias || ctaBlock.field_link[0].uri}${additonalLinkParams}`;
  const secondaryLinkTitle = ctaBlock.field_link_secondary[0]?.title;
  const secondaryLinkUri = ctaBlock.field_link_secondary[0]?.alias
    || ctaBlock.field_link_secondary[0]?.uri;
  const classes = classNames('Block', 'CTABlock');

  const contentColClasses = useMemo(() => {
    if (!imageAsBackground && image) {
      return 'col-12 col-md-6 col-lg-6';
    }

    return leftAlign
      ? 'col-12 left-align'
      : 'col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2';
  }, [image, imageAsBackground, leftAlign]);

  return (
    <Container>
      <div className={classes}>
        <div className={classNames('row', image && 'CTABlock__row-wrapper')}>
          {(!imageAsBackground && image) && (
            <div className="col-12 col-md-6 col-lg-6">
              <div className="CTABlock__image-wrapper">
                <Image
                  src={{
                    [SCREEN_MOBILE]: image.styles?.ratio_4_3_md,
                    [SCREEN_TABLET]: image.styles?.ratio_3_4_xl,
                    [SCREEN_NORMAL]: image.styles?.ratio_4_3_xl,
                    [SCREEN_WIDE]: image.styles?.ratio_4_3_xl,
                  }}
                  lazyload={false}
                  alt={image.alt}
                />
              </div>
            </div>
          )}

          <div className={contentColClasses}>
            <div className={classNames('CTABlock__wrapper', image && 'CTABlock__wrapper__hasImage')}>
              {(imageAsBackground && image) && (
                <div className="CTABlock__overlay">
                  <DynamicMediaItem
                    media={image}
                    imageAspectRatio="3_2"
                    className="CTABlock__background-image"
                  />
                </div>
              )}

              <div className="CTABlock__content">
                <h2 className="CTABlock__title">
                  {title}
                </h2>

                {body && (
                  <WysiwygField
                    className="CTABlock__body"
                    property="schema:text"
                    html={body}
                  />
                )}
              </div>

              <div className="CTABlock__links">
                <ButtonWrapper inverted href={linkUri}>
                  {linkTitle}
                </ButtonWrapper>

                {secondaryLinkUri && (
                  <ButtonWrapper type="secondary" inverted href={secondaryLinkUri}>
                    {secondaryLinkTitle}
                  </ButtonWrapper>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default CTABlock;
